import { ChannelInbox } from './shared/ChannelInbox';
import { PanelInbox } from './shared/PanelInbox';
import { ReceptionistCall } from './shared/ReceptionistCall';
import Reveal from './shared/Reveal';
import { useTranslation } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { config } from '../config/runtimeConfig';
import './page-layout.css';
import './contact.css';

/**
 * «Контакты».
 *
 * Прямые контакты, форма и мессенджеры. Салоны, карта и QR-коды живут в
 * отдельном разделе «Showroom» (#1337): приехать в салон и написать в
 * мессенджер — разные намерения.
 *
 * Телефон и почта — ссылками, а не кнопками: это переходы, и они обязаны
 * открываться в новой вкладке по Cmd+клику.
 *
 * ## Формы здесь нет, и это осознанно (#1330)
 *
 * Сначала тут стояли ДВЕ формы: рукописная пустышка (её `handleSubmit` делал
 * только `setSent(true)`) и рабочий `LeadFormEmbed` под заголовком «A form
 * that answers back». Пустышку убрали, а настоящая форма переехала на
 * CTA-полосу главной — там она стоит в момент решения, а не на странице
 * «напишите нам».
 *
 * Страница осталась про «поговорить сейчас»: прямые контакты, живой чат с
 * переключателем каналов и мессенджеры. Оставить заявку — на главной.
 */
/*
 * Куда ведёт живой чат — `config.LIVE_DEMO_TELEGRAM_URL` / `_WHATSAPP_URL`.
 * Своего демо-бота у витрины нет: по умолчанию Telegram ведёт на бота клиента
 * Andy's Motors (с их сайта andys-motors.com), на нём работает тот же
 * ассистент. WhatsApp появляется, только когда задан адрес: у Andy's он не
 * опубликован, а `+44 7700 900123` из конфига — вымышленный номер Ofcom.
 */

const ContactPage = () => {
  const { t } = useTranslation();
  const liveTelegramUrl = config.LIVE_DEMO_TELEGRAM_URL;
  const liveWhatsappUrl = config.LIVE_DEMO_WHATSAPP_URL;
  const liveClient = config.LIVE_DEMO_CLIENT_NAME;

  return (
    <div className="page contact">
      <div className="page__container">
        <Reveal as="div" className="contact__intro">
          {/* Подписи под заголовком нет: она пересказывала то, что страница
              показывает двумя окнами — «покупатель пишет откуда угодно, у
              команды это в одном инбоксе». Показ сильнее описания. */}
          <h1 className="contact__title">
            {t('showcase.chats.title', 'Chat with us')}
          </h1>
        </Reveal>

        {/* Две стороны ОДНОГО события рядом (#1330), в языке витрины (#1335,
            вариант «A» с холста): слева переключатель каналов, под ним окно
            переписки и плитка живого чата у клиента, справа подпись и инбокс
            команды. Окна без подложки — у мокапа своя рамка. */}
        <Reveal
          as="section"
          className="contact__flow reveal--stagger"
          delay={80}
        >
          <div className="contact__flow-side">
            <ChannelInbox />
            {/* Выход в живой чат: окно выше — показ, а здесь тот же ассистент
                отвечает по-настоящему. Без пояснения ссылка на чужого дилера
                читалась бы ошибкой. */}
            <section
              className="contact__tile contact__live"
              aria-labelledby="contact-live-title"
            >
              <div className="contact__live-text">
                <h2 id="contact-live-title" className="contact__live-title">
                  {t('showcase.chats.liveTitle', 'Live at {client}').replace(
                    '{client}',
                    liveClient
                  )}
                </h2>
                <p className="contact__live-lead">
                  {liveWhatsappUrl
                    ? t(
                        'showcase.chats.liveLeadBoth',
                        'Try the same assistant on Telegram or WhatsApp.'
                      )
                    : t(
                        'showcase.chats.liveLead',
                        'Try the same assistant on Telegram.'
                      )}
                </p>
              </div>
              {/* Таблетки со стрелкой — одно семейство кнопок на странице:
                  Telegram чёрная (синяя на странице — звонок), WhatsApp с
                  обводкой — две основные рядом спорили бы. */}
              <div className="contact__live-actions">
                <Button
                  as="a"
                  href={liveTelegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="dark"
                  size="sm"
                  arrow
                >
                  {t('showcase.chats.liveCta', 'Open Telegram')}
                </Button>
                {liveWhatsappUrl && (
                  <Button
                    as="a"
                    href={liveWhatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outline"
                    size="sm"
                    arrow
                  >
                    {t('showcase.chats.liveCtaWhatsapp', 'Open WhatsApp')}
                  </Button>
                )}
              </div>
            </section>
          </div>

          <div className="contact__flow-side">
            {/* Подпись НАД панелью, обычным блоком: наклейка с наклоном,
                наезжавшая на окно, была единственной такой на витрине. */}
            <div className="contact__flow-caption">
              <h2 className="contact__flow-caption-title">
                {t(
                  'showcase.chats.teamTitle',
                  'And this is what your team sees'
                )}
              </h2>
              <p className="contact__flow-caption-lead">
                {/* Дефолт обязателен: без него `t()` возвращает САМ КЛЮЧ. */}
                {t(
                  'showcase.chats.teamLead',
                  'One inbox for every channel. The assistant answers on its own, your team steps into any conversation, and nothing has to be chased across apps.'
                )}
              </p>
            </div>
            <PanelInbox />
          </div>
        </Reveal>
      </div>

      {/* Звонок — белой полосой во всю ширину со скруглёнными верхними
          углами, как подвал (#1335): последний показ страницы, и он стоит на
          своей плоскости. Внутри — сетка страницы: слева текст по центру
          колонки, справа телефон
          целиком, ничего за ним. */}
      <Reveal as="section" className="contact__call" delay={160}>
        <div className="page__container contact__call-inner">
          <div className="contact__call-copy">
            <h2 className="contact__call-title">
              {t('showcase.chats.callTitle', 'The assistant answers the phone')}
            </h2>
            <p className="contact__call-lead">
              {t(
                'showcase.chats.callLead',
                'The showroom closes at six. The assistant answers the phone, checks the car is still there and books the visit — press call and try it yourself.'
              )}
            </p>
          </div>
          <ReceptionistCall />
        </div>
      </Reveal>
    </div>
  );
};

export default ContactPage;
